import { supabase } from "@/integrations/supabase/client";

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Format seconds into H:MM:SS or M:SS */
const formatSeconds = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type AiContextStatus = "up_to_date" | "outdated" | "missing";

export interface KnowledgeBaseSummaryContent {
  language: string;
  contentType: 'summary' | 'full_book';
  hasAiContext: boolean;
  wordCount: number;
  chapterCount: number;
  summaryContentId: number;
  aiContextStatus: AiContextStatus;
  embeddingsStatus: AiContextStatus;
}

export interface KnowledgeBaseBook {
  id: number;
  title: string;
  coverImage: string | null;
  isActive: boolean;
  categories: string[];
  updatedAt: string;
  summaryContents: KnowledgeBaseSummaryContent[];
  embeddingsByLanguage: Record<string, number>;
  totalEmbeddings: number;
}

export interface KnowledgeBaseStats {
  totalBooks: number;
  booksWithAiContext: number;
  booksWithEmbeddings: number;
  totalChunks: number;
  fullyReady: number;
  aiContextCoverage: number;
  embeddingCoverage: number;
}

export interface KnowledgeBaseFilters {
  search: string;
  aiContextStatus: "all" | "has" | "missing";
  embeddingStatus: "all" | "has" | "missing";
  language: string;
  category: string;
}

export interface KnowledgeBaseBooksResult {
  books: KnowledgeBaseBook[];
  totalCount: number;
}

// ─── Stats ───────────────────────────────────────────────────────────────────

export const fetchKnowledgeBaseStats = async (): Promise<KnowledgeBaseStats> => {
  // Run 3 queries in parallel
  // Use head:true for embeddings count to bypass PostgREST max_rows (1000) limit
  const [booksRes, summaryRes, embeddingsCountRes] = await Promise.all([
    supabase.from("books").select("id", { count: "exact", head: true }),
    supabase.from("summary_contents").select("book_id, ai_context, embeddings_updated_at"),
    supabase.from("content_embeddings" as any).select("*", { count: "exact", head: true }),
  ]);

  const totalBooks = booksRes.count ?? 0;
  const totalChunks = embeddingsCountRes.count ?? 0;

  const booksWithContext = new Set<number>();
  const booksWithEmb = new Set<number>();
  if (summaryRes.data) {
    for (const row of summaryRes.data) {
      if (row.ai_context && row.ai_context.trim().length > 0 && row.book_id) {
        booksWithContext.add(row.book_id);
      }
      if (row.embeddings_updated_at && row.book_id) {
        booksWithEmb.add(row.book_id);
      }
    }
  }

  // Fully ready = has both context AND embeddings
  const fullyReady = [...booksWithContext].filter((id) => booksWithEmb.has(id)).length;

  return {
    totalBooks,
    booksWithAiContext: booksWithContext.size,
    booksWithEmbeddings: booksWithEmb.size,
    totalChunks,
    fullyReady,
    aiContextCoverage: totalBooks > 0 ? Math.round((booksWithContext.size / totalBooks) * 100) : 0,
    embeddingCoverage: totalBooks > 0 ? Math.round((booksWithEmb.size / totalBooks) * 100) : 0,
  };
};

// ─── Books List ──────────────────────────────────────────────────────────────

export const fetchKnowledgeBaseBooks = async (
  page: number,
  pageSize: number,
  filters: KnowledgeBaseFilters,
  sortField: string = "title",
  sortAsc: boolean = true
): Promise<KnowledgeBaseBooksResult> => {
  // 1. Fetch all books (with categories)
  let booksQuery = supabase
    .from("books")
    .select(
      `id, title, cover_image, is_active, updated_at,
       books_categories(categories(id, name))`,
      { count: "exact" }
    )
    .order("title", { ascending: true });

  if (filters.search) {
    booksQuery = booksQuery.ilike("title", `%${filters.search}%`);
  }

  const { data: booksData, error: booksError } = await booksQuery;
  if (booksError) throw booksError;
  if (!booksData || booksData.length === 0) return { books: [], totalCount: 0 };

  const bookIds = booksData.map((b) => b.id);

  // 2. Fetch summary_contents for these books (including staleness timestamps)
  const { data: summaryData } = await supabase
    .from("summary_contents")
    .select("id, book_id, language, content_type, ai_context, ai_context_updated_at, embeddings_updated_at")
    .in("book_id", bookIds);

  // 3. Fetch chapters counts + latest updated_at per summary_content
  const summaryContentIds = (summaryData || []).map((sc) => sc.id);
  let chapterCounts: Record<number, number> = {};
  let chapterLatestUpdated: Record<number, string> = {};
  if (summaryContentIds.length > 0) {
    const { data: chaptersData } = await supabase
      .from("chapters")
      .select("summary_content_id, updated_at")
      .in("summary_content_id", summaryContentIds);
    if (chaptersData) {
      for (const ch of chaptersData) {
        if (ch.summary_content_id) {
          chapterCounts[ch.summary_content_id] = (chapterCounts[ch.summary_content_id] || 0) + 1;
          const prev = chapterLatestUpdated[ch.summary_content_id];
          if (!prev || ch.updated_at > prev) {
            chapterLatestUpdated[ch.summary_content_id] = ch.updated_at;
          }
        }
      }
    }
  }

  // 4. Fetch embedding counts per book+language (paginated to bypass max_rows limit)
  const embeddingsByBookLang: Record<number, Record<string, number>> = {};
  const embeddingTotalByBook: Record<number, number> = {};
  {
    const pageSize = 1000;
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      const { data: embeddingData } = await supabase
        .from("content_embeddings" as any)
        .select("book_id, language")
        .in("book_id", bookIds)
        .range(from, from + pageSize - 1);

      if (embeddingData) {
        for (const row of embeddingData as any[]) {
          if (!embeddingsByBookLang[row.book_id]) embeddingsByBookLang[row.book_id] = {};
          embeddingsByBookLang[row.book_id][row.language] =
            (embeddingsByBookLang[row.book_id][row.language] || 0) + 1;
          embeddingTotalByBook[row.book_id] = (embeddingTotalByBook[row.book_id] || 0) + 1;
        }
      }

      hasMore = (embeddingData?.length ?? 0) === pageSize;
      from += pageSize;
    }
  }

  // 5. Assemble books
  let assembledBooks: KnowledgeBaseBook[] = booksData.map((book) => {
    const summaries = (summaryData || []).filter((sc) => sc.book_id === book.id);
    const categories = (book.books_categories as any[])
      ?.map((bc: any) => bc.categories?.name)
      .filter(Boolean) || [];

    const summaryContents: KnowledgeBaseSummaryContent[] = summaries.map((sc) => {
      const hasContext = !!(sc.ai_context && sc.ai_context.trim().length > 0);
      const latestChapterUpdate = chapterLatestUpdated[sc.id];
      const contextTs = sc.ai_context_updated_at;
      const embeddingsTs = sc.embeddings_updated_at;

      // AI context status: missing > outdated (chapters changed after context) > up_to_date
      let aiContextStatus: AiContextStatus = "missing";
      if (hasContext && contextTs) {
        aiContextStatus = latestChapterUpdate && latestChapterUpdate > contextTs
          ? "outdated"
          : "up_to_date";
      }

      // Embeddings status: missing > outdated (context changed after embeddings) > up_to_date
      let embeddingsStatus: AiContextStatus = "missing";
      if (embeddingsTs) {
        embeddingsStatus = contextTs && contextTs > embeddingsTs
          ? "outdated"
          : "up_to_date";
      }

      return {
        language: sc.language || "en",
        contentType: (sc.content_type as 'summary' | 'full_book') || 'summary',
        hasAiContext: hasContext,
        wordCount: sc.ai_context ? sc.ai_context.trim().split(/\s+/).length : 0,
        chapterCount: chapterCounts[sc.id] || 0,
        summaryContentId: sc.id,
        aiContextStatus,
        embeddingsStatus,
      };
    });

    return {
      id: book.id,
      title: book.title,
      coverImage: book.cover_image,
      isActive: book.is_active,
      categories,
      updatedAt: book.updated_at,
      summaryContents,
      embeddingsByLanguage: embeddingsByBookLang[book.id] || {},
      totalEmbeddings: embeddingTotalByBook[book.id] || 0,
    };
  });

  // 6. Apply filters
  if (filters.aiContextStatus === "has") {
    assembledBooks = assembledBooks.filter((b) => b.summaryContents.some((sc) => sc.hasAiContext));
  } else if (filters.aiContextStatus === "missing") {
    assembledBooks = assembledBooks.filter(
      (b) => b.summaryContents.length === 0 || b.summaryContents.every((sc) => !sc.hasAiContext)
    );
  }

  if (filters.embeddingStatus === "has") {
    assembledBooks = assembledBooks.filter((b) => b.totalEmbeddings > 0);
  } else if (filters.embeddingStatus === "missing") {
    assembledBooks = assembledBooks.filter((b) => b.totalEmbeddings === 0);
  }

  if (filters.language) {
    assembledBooks = assembledBooks.filter((b) =>
      b.summaryContents.some((sc) => sc.language === filters.language)
    );
  }

  if (filters.category) {
    assembledBooks = assembledBooks.filter((b) => b.categories.includes(filters.category));
  }

  // 7. Sort
  assembledBooks.sort((a, b) => {
    let cmp = 0;
    if (sortField === "title") {
      cmp = a.title.localeCompare(b.title);
    } else if (sortField === "totalEmbeddings") {
      cmp = a.totalEmbeddings - b.totalEmbeddings;
    } else if (sortField === "updatedAt") {
      cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    } else if (sortField === "summaryContents") {
      const aChapters = a.summaryContents.reduce((sum, sc) => sum + sc.chapterCount, 0);
      const bChapters = b.summaryContents.reduce((sum, sc) => sum + sc.chapterCount, 0);
      cmp = aChapters - bChapters;
    }
    return sortAsc ? cmp : -cmp;
  });

  // 8. Paginate
  const totalCount = assembledBooks.length;
  const start = (page - 1) * pageSize;
  const paginatedBooks = assembledBooks.slice(start, start + pageSize);

  return { books: paginatedBooks, totalCount };
};

// ─── Pre-fill AI Context from Chapters ───────────────────────────────────────

export const prefillAiContextFromChapters = async (
  bookId: number
): Promise<{ languagesUpdated: number; totalChapters: number }> => {
  // Fetch summary_contents for this book
  const { data: summaries, error: scError } = await supabase
    .from("summary_contents")
    .select("id, language, ai_context")
    .eq("book_id", bookId);

  if (scError) throw scError;
  if (!summaries || summaries.length === 0) {
    throw new Error("No summary contents found for this book");
  }

  const summaryIds = summaries.map((sc) => sc.id);

  // Fetch chapters for all summary_contents
  const { data: chapters, error: chError } = await supabase
    .from("chapters")
    .select("id, label, title, content, start_time, position, summary_content_id")
    .in("summary_content_id", summaryIds)
    .order("position", { ascending: true });

  if (chError) throw chError;

  let languagesUpdated = 0;
  let totalChapters = 0;

  for (const sc of summaries) {
    const scChapters = (chapters || [])
      .filter((ch) => ch.summary_content_id === sc.id)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

    if (scChapters.length === 0) continue;

    totalChapters += scChapters.length;

    // Build chapter heading with label and optional audio start time
    const nonChapterLabels = /^(introduction|conclusion|tawpna|hmuhchhuahna)$/i;
    const numberedChapterCount = scChapters.filter((ch) => !nonChapterLabels.test(ch.label?.trim())).length;
    let chapterIdx = 0;
    const text = scChapters
      .map((ch) => {
        const isNumberedChapter = !nonChapterLabels.test(ch.label?.trim());
        if (isNumberedChapter) chapterIdx++;
        const label = ch.label || `Chapter ${chapterIdx} of ${numberedChapterCount}`;
        const heading = `## ${label}: ${ch.title}`;
        const startTimeLine =
          ch.start_time != null
            ? `[Audio: starts at ${formatSeconds(ch.start_time)}]\n\n`
            : "";
        return `${heading}\n${startTimeLine}\n${ch.content}`;
      })
      .join("\n\n");

    const { error: updateError } = await supabase
      .from("summary_contents")
      .update({ ai_context: text, ai_context_updated_at: new Date().toISOString() })
      .eq("id", sc.id);

    if (updateError) throw updateError;
    languagesUpdated++;
  }

  return { languagesUpdated, totalChapters };
};

// ─── Generate Embeddings ─────────────────────────────────────────────────────

export const generateEmbeddingsForBook = async (
  bookId: number,
  language?: string
): Promise<{ chunksProcessed: number }> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("Not authenticated");

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/generate-embeddings`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: anonKey,
    },
    body: JSON.stringify({ bookId, language }),
  });

  const result = await res.json();

  if (!res.ok) {
    throw new Error(result?.error || `Server error: ${res.status}`);
  }

  return { chunksProcessed: result.chunksProcessed || 0 };
};
